import { useCallback, useEffect } from 'react';
import { AIFeature } from '@/common/interface';
import { AppEvent, emitter } from '@/plugin/mitt';
import authService from '@/services/authService';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchAIUsageRequest } from '../store/slices/aiUsageSlice';

/**
 * Features whose whole screen is AI (chat), so they are locked when not in the plan. Every other
 * feature stays open: practice is saved, only grading is skipped, and AI generation actions get
 * the plan-limit modal from the API.
 */
const LOCKABLE_FEATURES: AIFeature[] = ['conversation'];

/** AI usage of the signed-in learner's plan; fetched once and shared through the store. */
export const useAIUsage = () => {
    const dispatch = useAppDispatch();
    const { usage, status } = useAppSelector((state) => state.aiUsage);
    const hasToken = authService.hasSession();

    useEffect(() => {
        if (hasToken && status === 'idle') {
            dispatch(fetchAIUsageRequest());
        }
    }, [hasToken, status, dispatch]);

    // Until the plan is known nothing is locked; the backend still enforces the limits.
    const isReady = !hasToken || status === 'succeeded' || status === 'failed';

    const getUsage = useCallback(
        (feature?: AIFeature | null) => (feature && usage?.features.find((item) => item.feature === feature)) || null,
        [usage],
    );

    const isLocked = useCallback(
        (feature?: AIFeature | null) =>
            !!feature && LOCKABLE_FEATURES.includes(feature) && getUsage(feature)?.limit === 0,
        [getUsage],
    );

    /** True when work submitted for the feature is saved without a grade (not in plan or out of quota). */
    const isUngraded = useCallback(
        (feature?: AIFeature | null) => {
            const item = getUsage(feature);
            return !!item && item.limit !== null && (item.limit === 0 || item.remaining === 0);
        },
        [getUsage],
    );

    const showLocked = useCallback(
        (feature: AIFeature) => {
            emitter.emit(AppEvent.AI_PLAN_LIMIT, {
                code: 'AI_FEATURE_NOT_IN_PLAN',
                message: '',
                feature,
                plan: usage?.plan ?? null,
            });
        },
        [usage],
    );

    const refresh = useCallback(() => {
        dispatch(fetchAIUsageRequest());
    }, [dispatch]);

    return { usage, isReady, getUsage, isLocked, isUngraded, showLocked, refresh };
};
